// Real implementation of ISubmissionFlowApi: Registration.WebApi for the flow, and the
// ProfileManagement API for photos and resumes (profileManagement.ts). The page coordinates
// the two: it uploads to PM, then closes the step with Registration, which checks what PM
// wrote before it counts the step as done (docs/14, "Progress and state").
//
// Built server-side: casting calls, submission info, steps 1 and 1b, and step 2 (draft,
// materials, home location). Steps 3 and 4 below are the contract only.
// getRequesterLocation reuses the existing References country/code endpoint.

import * as http from "../../http";
import { getForcedCountryCode } from "./config";
import {
  getRequesterCountryCode,
  getSupportedCitiesCountries,
} from "../References.api";
import { getSubmissionAuthHeader } from "../../../helpers/submissionAuth";
import * as pm from "./profileManagement";
import * as phone from "./phoneVerification";
import { accountNameOfTab, continueClassicActor, findClassicActor } from "./loginHandOff";
import { readPersistedFlow } from "../../../components/Submission/common/persistedFlow";
import {
  IAgencyOptionDto,
  IAgentOptionDto,
  IChangeRepresentationResponse,
  IUnsubscribeResponse,
  IBreakdownCityDto,
  IBreakdownSubmissionInfoDto,
  ICastingCallListDto,
  ICastingCallsQuery,
  ICheckoutStatusDto,
  IDirectCheckoutResponse,
  IHoldSubmissionDto,
  IHoldSubmissionRequest,
  IHomeLocationDto,
  IMediaAssetDto,
  IPaywallExposureRequest,
  IExperimentExposureResponse,
  IPaywallOfferDto,
  IRepresentationDto,
  IResendCodeResponse,
  ISaveMaterialsRequest,
  ISaveForLaterRequest,
  ISaveForLaterResponse,
  FinishLaterEmailStatus,
  ISendSubmissionRequest,
  ISendSubmissionResponse,
  ISetHomeLocationRequest,
  ISetRepresentationRequest,
  IPhoneStatusDto,
  ISendPhoneCodeRequest,
  ISendPhoneCodeResponse,
  ISignupStateResponse,
  IStartCheckoutRequest,
  IStartCheckoutResponse,
  IStartSignupRequest,
  IStartSignupResponse,
  ISubmissionDraftDto,
  ISubmissionHomeDto,
  ISubmissionFlowApi,
  ISubmissionGeoGuessDto,
  ISubmissionMembershipDto,
  ISubmissionReviewDto,
  IAccountNameDto,
  IClassicSessionDto,
  ITabSignInDto,
  IUploadMediaRequest,
  IVerifyEmailResponse,
  IVerifyPhoneOptions,
  IVerifyPhoneResponse,
  MediaKind,
  Step4Variant,
} from "./types";

const api = () => `${process.env.REACT_APP_REG_Api}/breakdowns`;
const signupApi = () => `${process.env.REACT_APP_REG_Api}/Signup/submission`;

// Registration's draft: the materials as the PM API's own ids (docs/14, Step 2).
interface IServerDraftDto {
  submissionId: number;
  breakdownId: string | null;
  roleId: string | null;
  photoNumber?: number | null;
  resumeId?: number | null;
  noteToCastingDirector?: string;
  ageAttested: boolean;
  phoneVerified: boolean;
  status: "Draft" | "Saved" | "Sent";
  holdUntilUtc?: string;
  updatedAtUtc: string;
  home?: ISubmissionHomeDto | null;
}

// The page's assets are the PM ids: a photo asset is its slot, a resume asset its resume id.
// A reference the PM API can't resolve still comes back, without a URL, so a slow or failing
// PM call never loses the draft.
const toDraftDto = async (draft: IServerDraftDto, resolve: boolean): Promise<ISubmissionDraftDto> => {
  const photoNumber = draft.photoNumber || undefined;
  const resumeId = draft.resumeId || undefined;
  const bare = (assetId: number, kind: MediaKind): IMediaAssetDto => ({
    assetId,
    kind,
    fileName: "",
    sizeBytes: 0,
    uploadedAtUtc: draft.updatedAtUtc,
  });
  const safely = async (lookup: () => Promise<IMediaAssetDto | undefined>, fallback: IMediaAssetDto) => {
    if (!resolve) return fallback;
    try {
      return (await lookup()) || fallback;
    } catch (e) {
      return fallback;
    }
  };
  return {
    submissionId: draft.submissionId,
    breakdownId: draft.breakdownId,
    roleId: draft.roleId,
    photo: photoNumber
      ? await safely(() => pm.findPhoto(photoNumber), bare(photoNumber, "photo"))
      : undefined,
    resume: resumeId
      ? await safely(() => pm.findResume(resumeId), bare(resumeId, "resume"))
      : undefined,
    noteToCastingDirector: draft.noteToCastingDirector || "",
    ageAttested: draft.ageAttested,
    phoneVerified: draft.phoneVerified,
    status: draft.status,
    holdUntilUtc: draft.holdUntilUtc,
    updatedAtUtc: draft.updatedAtUtc,
    home: draft.home || undefined,
  };
};

// The number step 3 last texted in this tab: the code is checked against it.
let lastPhoneSent = "";

// POST with the account JWT attached (everything after email verification).
const authedPost = <T>(url: string, body: any) =>
  http.post<T>(url, body, {
    method: "post",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      CWB: "1",
      ...getSubmissionAuthHeader(),
    },
    body: JSON.stringify(body),
  });

const authedPatch = <T>(url: string, body: any) =>
  http.patch<T>(url, body, {
    method: "PATCH",
    mode: "cors",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      CWB: "1",
      ...getSubmissionAuthHeader(),
    },
    body: JSON.stringify(body),
  });

const authedGet = <T>(url: string) =>
  http.get<T>(url, {
    method: "GET",
    mode: "cors",
    credentials: "include",
    headers: {
      CWB: "1",
      ...getSubmissionAuthHeader(),
    },
  });

const realSubmissionFlowApi: ISubmissionFlowApi = {
  async getBreakdownSubmissionInfo(
    breakdownId: string,
    roleId?: string
  ): Promise<IBreakdownSubmissionInfoDto> {
    // Only append roleId when it's a real value — `?roleId=${undefined}`
    // would send the literal string "undefined" (same guard as
    // References.api.ts getRequesterCountryCode).
    // Refs go through verbatim (zero padding intact) — never parsed.
    const bd = encodeURIComponent(breakdownId);
    const url = roleId
      ? `${api()}/${bd}/submission-info?roleid=${encodeURIComponent(roleId)}`
      : `${api()}/${bd}/submission-info`;
    const resp = await http.get<IBreakdownSubmissionInfoDto>(url);
    return resp.data;
  },

  async getCastingCalls(query?: ICastingCallsQuery): Promise<ICastingCallListDto> {
    // The country rides in the path (/casting-calls/CA) only when the URL named
    // one; otherwise the server decides from the IP, city and province included.
    // It also applies its switch policy and caps the list. Only `type` is a param.
    const params = new URLSearchParams();
    if (query && typeof query.type === "number") params.set("type", String(query.type));
    const qs = params.toString();
    const country = query && query.countryCode ? `/${encodeURIComponent(query.countryCode)}` : "";
    const url = `${api()}/casting-calls${country}${qs ? `?${qs}` : ""}`;
    const resp = await http.get<ICastingCallListDto>(url);
    return resp.data;
  },

  async getRequesterLocation(): Promise<ISubmissionGeoGuessDto> {
    // ?ctry=US short-circuits the lookup entirely — no point spending a round
    // trip on an answer we are about to overwrite.
    const forced = getForcedCountryCode();
    if (forced) return { countryCode: forced };

    const resp = await getRequesterCountryCode();
    const details: any = resp.data || {};
    return {
      ip: details.ip,
      countryCode: details.countryCode || "",
      regionCode: details.regionCode,
      // City-level guess is not available from the legacy endpoint; the
      // account-creation defaults will fall back to asking the user.
      cityId: undefined,
      cityName: undefined,
    };
  },

  async getCityOptions(): Promise<IBreakdownCityDto[]> {
    // Reuses the existing References endpoint rather than adding one: it
    // already returns every supported city with its country, which is exactly
    // the picker's option list.
    const resp = await getSupportedCitiesCountries();
    const rows: any[] = resp.data || [];
    return rows.map((row) => ({
      cityId: row.cityId,
      cityName: row.cityName,
      provinceId: row.provinceId,
      countryId: row.countryId,
      countryCode: row.countryCode,
    }));
  },

  async setHomeLocation(payload: ISetHomeLocationRequest): Promise<IHomeLocationDto> {
    const resp = await authedPatch<IHomeLocationDto>(
      `${signupApi()}/home-location`,
      payload
    );
    return resp.data;
  },

  async getHome(): Promise<ISubmissionHomeDto> {
    const resp = await authedGet<ISubmissionHomeDto>(`${signupApi()}/home-location`);
    return resp.data;
  },

  // The sign-in service, not Registration: loginHandOff.ts (nothing kept outside the tab).
  findClassicSession(): Promise<IClassicSessionDto | null> {
    return findClassicActor();
  },

  continueClassicSession(): Promise<ITabSignInDto> {
    return continueClassicActor();
  },

  getAccountName(): Promise<IAccountNameDto | null> {
    return accountNameOfTab();
  },

  async checkEmailAvailable(email: string): Promise<{ available: boolean }> {
    const resp = await http.get<{ available: boolean }>(
      `${signupApi()}/email/available?email=${encodeURIComponent(email)}`
    );
    return resp.data;
  },

  async startSignup(payload: IStartSignupRequest): Promise<IStartSignupResponse> {
    const resp = await http.post<IStartSignupResponse>(`${signupApi()}/start`, payload);
    return resp.data;
  },

  async verifyEmailCode(sessionId: string, code: string): Promise<IVerifyEmailResponse> {
    const resp = await http.post<IVerifyEmailResponse>(
      `${signupApi()}/verify-email/${encodeURIComponent(sessionId)}/${encodeURIComponent(code)}`,
      {}
    );
    return resp.data;
  },

  async resendEmailCode(sessionId: string): Promise<IResendCodeResponse> {
    const resp = await http.post<IResendCodeResponse>(
      `${signupApi()}/resend-email-code/${encodeURIComponent(sessionId)}`,
      {}
    );
    return resp.data;
  },

  async getSignupState(sessionId: string): Promise<ISignupStateResponse> {
    const resp = await http.get<ISignupStateResponse>(
      `${signupApi()}/state?sessionId=${encodeURIComponent(sessionId)}`
    );
    return resp.data;
  },

  async createDraft(
    breakdownId: string | null,
    roleId: string | null
  ): Promise<ISubmissionDraftDto> {
    // Opening step 2 (or coming back to it): the draft, with its photo and resume read back
    // from the PM API so a refresh shows them again.
    // Where the actor came from (the tData cookie): a new draft keeps it, the only record of an
    // existing member's source (Signup/docs/35 S6, R6). The server ignores it on re-entry.
    let trackingData: object | undefined;
    try {
      // Required here, not imported: the helpers barrel loads i18n, which this API module (and
      // the tests that load it) otherwise never needs.
      // eslint-disable-next-line global-require
      trackingData = require("../../../helpers").getTrackingData();
    } catch (e) {
      trackingData = undefined;
    }
    // The page Submit was clicked on (the casting calls list, the boost page, or a direct link):
    // the draft's source when no src or UTM came (Signup/docs/38 B29).
    const flow = readPersistedFlow();
    const resp = await authedPost<IServerDraftDto>(`${signupApi()}/draft`, {
      breakdownId,
      roleId,
      trackingData,
      entryPage: flow ? flow.entrySource : undefined,
    });
    return toDraftDto(resp.data, true);
  },

  async uploadMedia(payload: IUploadMediaRequest): Promise<IMediaAssetDto> {
    // Straight to the PM API with the account's token; Registration never sees the file.
    if (!payload.file) throw { status: 400, errorCode: "uploadFailed", message: "" };
    return payload.kind === "photo"
      ? pm.uploadPhoto(payload.file, payload.fileName)
      : pm.uploadResume(payload.file, payload.fileName, payload.mimeType);
  },

  async getExistingMedia(kind: MediaKind): Promise<IMediaAssetDto[]> {
    return kind === "photo" ? pm.listPhotos() : pm.listResumes();
  },

  async saveMaterials(
    submissionId: number,
    payload: ISaveMaterialsRequest
  ): Promise<ISubmissionDraftDto> {
    // Closes step 2: Registration checks the photo slot and resume against what the PM API
    // wrote, then records them. The page already holds the assets, so nothing is read back.
    const resp = await authedPost<IServerDraftDto>(
      `${signupApi()}/${submissionId}/materials`,
      {
        photoNumber: payload.photoAssetId,
        resumeId: payload.resumeAssetId,
        noteToCastingDirector: payload.noteToCastingDirector,
        ageAttested: payload.ageAttested,
      }
    );
    return toDraftDto(resp.data, false);
  },

  async saveForLater(submissionId: number, payload: ISaveForLaterRequest): Promise<ISaveForLaterResponse> {
    // Step 2 stays open: no MaterialsUtc, so the win-back Flow A goes on (docs/31).
    const resp = await authedPost<IServerDraftDto & { emailStatus: FinishLaterEmailStatus }>(
      `${signupApi()}/${submissionId}/materials/later`,
      {
        photoNumber: payload.photoAssetId,
        resumeId: payload.resumeAssetId,
        noteToCastingDirector: payload.noteToCastingDirector,
        ageAttested: payload.ageAttested,
        sendEmail: payload.sendEmail,
      }
    );
    return { draft: await toDraftDto(resp.data, false), emailStatus: resp.data.emailStatus || "failed" };
  },

  // Step 3 goes to the actor phone API (the membership site's, unchanged), then Registration
  // records the step (decided 2026-10-06, option A).
  async getPhoneStatus(): Promise<IPhoneStatusDto> {
    return phone.getPhoneStatus();
  },

  async sendPhoneCode(payload: ISendPhoneCodeRequest): Promise<ISendPhoneCodeResponse> {
    const number = phone.toE164(payload.phone);
    if (!phone.isPlausiblePhone(number)) throw { status: 400, errorCode: "invalidPhone", message: "" };
    await phone.sendPhoneCode(number, payload.recaptchaToken);
    lastPhoneSent = number;
    return {
      sent: true,
      maskedPhone: phone.maskPhone(number),
      resendCooldownSeconds: phone.PHONE_RESEND_SECONDS,
    };
  },

  async verifyPhoneCode(code: string, options: IVerifyPhoneOptions = {}): Promise<IVerifyPhoneResponse> {
    const number = options.phone ? phone.toE164(options.phone) : lastPhoneSent;
    await phone.verifyPhoneCode(number, code, options.recaptchaToken, options.trackingData);
    return { verified: true, phoneConfirmedAtUtc: new Date().toISOString() };
  },

  async recordPhone(submissionId: number): Promise<ISubmissionDraftDto> {
    const resp = await authedPost<IServerDraftDto>(`${signupApi()}/${submissionId}/phone`, {});
    return toDraftDto(resp.data, false);
  },

  // --- Step 4 --------------------------------------------------------------
  // None of these routes exist server-side yet; this file is the frontend half
  // of the contract. docs/17 lists what is reusable (PerformerTrial eligibility
  // and start/trial, Services/paid/client/bdsubmission) and what is new.

  async getSubmissionMembership(): Promise<ISubmissionMembershipDto> {
    const resp = await authedGet<ISubmissionMembershipDto>(`${signupApi()}/membership`);
    return resp.data;
  },

  async getSubmissionReview(submissionId: number): Promise<ISubmissionReviewDto> {
    const resp = await authedGet<ISubmissionReviewDto>(
      `${signupApi()}/${submissionId}/review`
    );
    const review = resp.data;
    // Registration keeps the PM ids only; the files come from the PM API, as on
    // step 2. Best effort: a card without its thumbnail still says what goes.
    const [photo, resume] = await Promise.all([
      review.photoNumber ? pm.findPhoto(review.photoNumber).catch(() => undefined) : undefined,
      review.resumeId ? pm.findResume(review.resumeId).catch(() => undefined) : undefined,
    ]);
    // "View breakdown details" opens the flow's own breakdown page: nothing in
    // the flow links to a classic page until it's over.
    const details = review.breakdownId
      ? `/submission/boost?bd=${encodeURIComponent(review.breakdownId)}${
          review.roleId ? `&roleid=${encodeURIComponent(review.roleId)}` : ""
        }`
      : undefined;
    return { ...review, photo: review.photo || photo, resume: review.resume || resume, breakdownDetailsUrl: details };
  },

  async unsubscribeWinBack(token: string): Promise<IUnsubscribeResponse> {
    const resp = await http.post<IUnsubscribeResponse>(`${signupApi()}/unsubscribe`, { token });
    return resp.data;
  },

  async winBackReturn(eref: string): Promise<void> {
    await http.post(`${signupApi()}/winback-return`, { eref });
  },

  // Representation lives in ProfileManagement (docs/30 §9): the search and the new agent go
  // there, then Registration records the roster on this submission, and on nothing else.
  async searchAgencies(query: string): Promise<IAgencyOptionDto[]> {
    return pm.searchAgencies(query);
  },

  async listAgencyAgents(agencyId: number): Promise<IAgentOptionDto[]> {
    return pm.listAgencyAgents(agencyId);
  },

  async setJobAlerts(subscribe: boolean): Promise<void> {
    await authedPost(`${signupApi()}/job-alerts`, { subscribe });
  },

  async setRepresentation(
    submissionId: number,
    payload: ISetRepresentationRequest
  ): Promise<IRepresentationDto> {
    // The default: no roster.
    if (payload.type === "selfRepresented") {
      const self = await authedPost<IChangeRepresentationResponse>(
        `${signupApi()}/${submissionId}/representation`,
        {}
      );
      return self.data.representation;
    }

    const name = (payload.agentName || "").trim();
    const space = name.indexOf(" ");
    const roster = await pm.addAgent({
      agencyId: payload.type === "agency" ? payload.agencyId : 0,
      agencyName: payload.agencyName,
      agentId: payload.agentId,
      agentFirstName: payload.agentId ? undefined : space > 0 ? name.slice(0, space) : name,
      agentLastName: payload.agentId ? undefined : space > 0 ? name.slice(space + 1).trim() : "",
      email: payload.agentId ? undefined : (payload.agentEmail || "").trim(),
      phone: payload.agentId ? undefined : (payload.agentPhone || "").trim(),
    });
    const resp = await authedPost<IChangeRepresentationResponse>(
      `${signupApi()}/${submissionId}/representation`,
      { rosterId: roster.rosterId }
    );
    return resp.data.representation;
  },

  async getPaywallOffer(variant: Step4Variant): Promise<IPaywallOfferDto> {
    const resp = await authedGet<IPaywallOfferDto>(
      `${signupApi()}/paywall-offer?variant=${encodeURIComponent(variant)}`
    );
    return resp.data;
  },

  async startCheckout(payload: IStartCheckoutRequest): Promise<IStartCheckoutResponse> {
    const resp = await authedPost<IStartCheckoutResponse>(
      `${signupApi()}/checkout`,
      payload
    );
    return resp.data;
  },

  async getCheckoutStatus(checkoutSessionId: string): Promise<ICheckoutStatusDto> {
    const resp = await authedGet<ICheckoutStatusDto>(
      `${signupApi()}/checkout/${encodeURIComponent(checkoutSessionId)}/status`
    );
    return resp.data;
  },

  async completeMockCheckout(): Promise<ICheckoutStatusDto> {
    // There is no such thing against a real backend: Stripe reports the outcome
    // by webhook. Rejecting loudly beats quietly resolving, which would let a
    // fake "paid" state through in a real environment.
    throw new Error(
      "completeMockCheckout is mock-only — real checkouts are confirmed by Stripe's webhook"
    );
  },

  async sendSubmission(
    submissionId: number,
    payload: ISendSubmissionRequest
  ): Promise<ISendSubmissionResponse> {
    const resp = await authedPost<ISendSubmissionResponse>(
      `${signupApi()}/${submissionId}/send`,
      payload
    );
    return resp.data;
  },

  async holdSubmission(
    submissionId: number,
    payload: IHoldSubmissionRequest
  ): Promise<IHoldSubmissionDto> {
    const resp = await authedPost<IHoldSubmissionDto>(
      `${signupApi()}/${submissionId}/hold`,
      payload
    );
    return resp.data;
  },

  async recordPaywallExposure(
    payload: IPaywallExposureRequest
  ): Promise<IExperimentExposureResponse> {
    const resp = await authedPost<IExperimentExposureResponse>(
      `${signupApi()}/experiment-exposure`,
      payload
    );
    return resp.data;
  },

  async recordCheckoutStarted(submissionId: number): Promise<void> {
    await authedPost<void>(`${signupApi()}/${submissionId}/checkout-started`, {});
  },

  async recordPlanDeclined(submissionId: number): Promise<void> {
    await authedPost<void>(`${signupApi()}/${submissionId}/plan-declined`, {});
  },

  async startDirectCheckout(submissionId: number, params: Record<string, string>): Promise<IDirectCheckoutResponse> {
    const resp = await authedPost<IDirectCheckoutResponse>(`${signupApi()}/${submissionId}/direct-checkout`, { params });
    return resp.data || {};
  },
};

export default realSubmissionFlowApi;
